/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import type { SearchResultItem } from '../../api/search/search.types';
import { getTargetUri } from '../utils/getTargetUri';
import { getCardRating } from '../utils/rating';
import { getPriceRange } from '../utils/price';
import { formatLocationName } from '../utils/formatLocationName';

import styles from './CardLinkHeading.module.scss';

interface CardLinkHeadingProps {
    item: SearchResultItem;
    id?: string;
    className?: string;
    gaTrackingId?: string;
    gaTrackingLocation?: string;
    /** Called when the link is clicked, before the browser opens it in a new tab */
    onLinkClick?: (item: SearchResultItem) => void;
}

export function CardLinkHeading({
    item,
    id,
    className,
    gaTrackingId,
    gaTrackingLocation,
    onLinkClick,
}: CardLinkHeadingProps) {
    const targetUri = getTargetUri(item);
    const { ratingAriaLabelShort } = getCardRating(item);
    const { priceRangeAriaLabel } = getPriceRange(item);

    const formattedLocationName = formatLocationName(item.name);
    const ariaLabel = `${formattedLocationName} ${priceRangeAriaLabel ? `/ ${priceRangeAriaLabel}` : ''} ${ratingAriaLabelShort ? `/ ${ratingAriaLabelShort}` : ''} (opens in a new tab)`;

    return (
        <h2 className={cx(styles.heading, className)} id={id}>
            {/* eslint-disable-next-line react/jsx-no-target-blank -- No noreferrer, so the facility page gets the search page as its referrer, e.g. for GA's page_referrer. noopener still keeps it from reaching back into this tab. */}
            <a
                className={styles.cardLink}
                href={targetUri}
                target="_blank"
                rel="noopener"
                data-ga-tracking-id={gaTrackingId}
                data-ga-tracking-value={formatLocationName(item.name)}
                data-ga-tracking-location={gaTrackingLocation}
                onClick={onLinkClick ? () => onLinkClick(item) : undefined}
                aria-label={ariaLabel}
            >
                {formattedLocationName}
            </a>
        </h2>
    );
}
